/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { FaRegCircleCheck } from "react-icons/fa6";
import ReactImageUploading from "react-images-uploading";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { Button } from "@material-tailwind/react";
import { AiFillPlusCircle } from "react-icons/ai";
import { FiTrash } from "react-icons/fi";
import ReactQuill from "react-quill";
import InputField from "../../../components/input";
import { addBlog, getCategoryBlogList } from "../../../service/api";
import { toast } from "react-toast";
import moment from "moment";

const validationSchema = yup.object().shape({
  name: yup.string().required(),
  description: yup.string().required("Description is required."),
  category: yup.object().required("Category is required"),
});

const AddBlog = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [profileImage, setProfileImage] = useState([]);
  const [file, setFile] = useState(null);

  useEffect(() => {
    functionGetBlogCategory();
  }, []);

  const functionGetBlogCategory = () => {
    setBlogLoading(true);
    getCategoryBlogList({ pagination: false })
      .then((res) => {
        if (res.status) {
          let tempArray = res.data.map((element) => ({
            value: element._id,
            label: element.name,
          }));

          setCategoryOptions(tempArray);
        }
      })
      .finally(() => {
        setBlogLoading(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      shortdescription: "",
      category: "",
      slug: "",
      seoTitle: "",
      seoDescription: "",
      seoKeywords: "",
      canonicalUrl: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      let formData = new FormData();
      formData.append("title", values.name);
      formData.append("description", values.description);
      formData.append("isShow", true);
      formData.append("startDate", moment(new Date()).format("YYYY-MM-DD"));
      formData.append("endDate", moment(new Date()).format("YYYY-MM-DD"));
      profileImage.forEach((img) => {
        if (img.file) {
          formData.append("image", img.file);
        }
      });
      formData.append("category", values.category?.value);
      formData.append("slug", values.slug);
      formData.append("seoTitle", values.seoTitle);
      formData.append("seoDescription", values.seoDescription);
      formData.append("seoKeywords", values.seoKeywords);
      formData.append("canonicalUrl", values.canonicalUrl);

      setLoading(true);
      addBlog(formData)
        .then((res) => {
          if (res.status) {
            toast.success(res.message);
            setFile(null);
            formik.resetForm();
            navigate("/blogs");
          } else {
            toast.error(res.message);
          }
        })
        .catch((error) => {
          toast.error(error.response.data.message || "Failed to add blog");
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const onImageChange = (imageList) => {
    console.log(imageList);
    setProfileImage(imageList);
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }, { size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "align",
    "blockquote",
    "code-block",
    "link",
    "image",
    "video",
  ];

  return (
    <section className="p-5 min-h-screen xl:min-h-full lg:min-h-full">
      <form onSubmit={formik.handleSubmit}>
        <div className="container-full-width">
          <div className="block xl:flex lg:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
                Add Blog
              </h1>
              <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
                Create a new blog post for the website.
              </h2>
            </div>
            <div className="flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
              <Link to="/blogs">
                <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap xl:whitespace-normal lg:whitespace-normal shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-5 py-3 rounded-md border border-authBorder text-sm font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out">
                  Back to Blogs
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={loading}
                className="w-full xl:w-auto lg:w-auto whitespace-nowrap xl:whitespace-normal lg:whitespace-normal shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out"
              >
                {loading ? "Please wait..." : "Add Blog"}
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-y-5 xl:gap-5 lg:gap-5">
              <div className="col-span-2">
                <div className="bg-white rounded-lg border border-authBorder">
                  <div className="px-5 py-3 border-b border-authBorder">
                    <h5 className="text-lg font-ibm-bold text-black">
                      Blog Details
                    </h5>
                    <p className="text-sm font-ibm-regular text-greyText">
                      Add the title, category, and descriptions for this post.
                    </p>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-5">
                      <div className="col-span-2">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            Blog Title <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1">
                            <InputField
                              type="text"
                              name="name"
                              id="name"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              error={
                                formik.errors.name && formik.touched.name
                                  ? true
                                  : false
                              }
                              placeholder="Write the blog title"
                            />
                          </div>
                          {formik.errors.name && formik.touched.name && (
                            <div className="text-red-500 text-xs mt-1">
                              {formik.errors.name}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            Category <span className="text-red-500">*</span>
                          </label>
                          <div className="relative mt-1">
                            <Select
                              options={categoryOptions}
                              placeholder="Select category"
                              className={`${formik.errors.category && "error"}`}
                              classNamePrefix="dropdown"
                              name="category"
                              id="category"
                              value={formik.values.category}
                              onChange={(selectedOption) =>
                                formik.setFieldValue("category", selectedOption)
                              }
                            />
                          </div>
                          {formik.errors.category &&
                            formik.touched.category && (
                              <div className="text-red-500 text-xs mt-1">
                                {formik.errors.category.label}
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="col-span-3">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            Blog Description{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="mt-1 blog-editor">
                            <ReactQuill
                              theme="snow"
                              value={formik.values.description}
                              onChange={(html) =>
                                formik.setFieldValue("description", html)
                              }
                              modules={quillModules}
                              formats={quillFormats}
                              placeholder="Write the blog description here"
                            />
                          </div>
                          {formik.errors.description &&
                            formik.touched.description && (
                              <div className="text-red-500 text-xs mt-1">
                                {formik.errors.description}
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO DETAILS SECTION */}
                <div className="bg-white rounded-lg border border-authBorder mt-5">
                  <div className="px-5 py-3 border-b border-authBorder">
                    <h5 className="text-lg font-ibm-bold text-black">
                      SEO Details (Optional)
                    </h5>
                    <p className="text-sm font-ibm-regular text-greyText">
                      Add custom slug and meta tags for search engines.
                    </p>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            Slug
                          </label>
                          <div className="mt-1">
                            <InputField
                              type="text"
                              name="slug"
                              id="slug"
                              value={formik.values.slug}
                              onChange={formik.handleChange}
                              placeholder="e.g., my-awesome-blog"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            SEO Title
                          </label>
                          <div className="mt-1">
                            <InputField
                              type="text"
                              name="seoTitle"
                              id="seoTitle"
                              value={formik.values.seoTitle}
                              onChange={formik.handleChange}
                              placeholder="SEO Meta Title"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            SEO Description
                          </label>
                          <div className="mt-1">
                            <textarea
                              name="seoDescription"
                              id="seoDescription"
                              value={formik.values.seoDescription}
                              onChange={formik.handleChange}
                              className="w-full px-4 py-2 border border-authBorder rounded-md focus:outline-none focus:border-logoYellow transition-colors duration-300 min-h-[100px]"
                              placeholder="SEO Meta Description"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            SEO Keywords
                          </label>
                          <div className="mt-1">
                            <InputField
                              type="text"
                              name="seoKeywords"
                              id="seoKeywords"
                              value={formik.values.seoKeywords}
                              onChange={formik.handleChange}
                              placeholder="comma, separated, keywords"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="form-group">
                          <label className="text-sm font-ibm-regular text-greyText">
                            Canonical URL
                          </label>
                          <div className="mt-1">
                            <InputField
                              type="text"
                              name="canonicalUrl"
                              id="canonicalUrl"
                              value={formik.values.canonicalUrl}
                              onChange={formik.handleChange}
                              placeholder="e.g., https://www.insurancecareershq.com/blogs/my-blog"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1">
                <div className="bg-white rounded-lg border border-authBorder">
                  <div className="px-5 py-3 border-b border-authBorder">
                    <h5 className="text-lg font-ibm-bold text-black">
                      Blog Cover Image
                    </h5>
                    <p className="text-sm font-ibm-regular text-greyText">
                      Attach the cover image(s) for this blog
                    </p>
                  </div>
                  <div className="p-5">
                    <ReactImageUploading
                      multiple
                      value={profileImage}
                      onChange={onImageChange}
                      maxNumber={5}
                      dataURLKey="data_url"
                    >
                      {({ imageList, onImageUpload, onImageRemove }) => (
                        <div className="grid grid-cols-1 gap-5">
                          <div className="col-span-1">
                            <div
                              className="min-w-36 h-productImage rounded-md bg-[#F5F5F5] bg-opacity-50 border border-authBorder cursor-pointer hover:border-logoYellow transition-all duration-300 ease-in-out p-5"
                              onClick={onImageUpload}
                            >
                              <AiFillPlusCircle className="w-8 h-8 text-greyText mx-auto" />
                              <div className="mt-2">
                                <p className="text-base font-ibm-regular text-black text-center">
                                  Click here to upload images
                                </p>
                                <p className="text-sm font-ibm-regular text-greyText text-center mt-1">
                                  (JPG, JPEG, PNG)
                                </p>
                              </div>
                            </div>
                          </div>
                          {imageList.map((image, index) => (
                            <div className="col-span-1 relative" key={index}>
                              <img
                                src={image["data_url"]}
                                className="w-full h-[25vh] object-cover rounded-md"
                                alt="Product"
                              />
                              <div className="absolute top-2 right-2">
                                <Button
                                  className="p-2 rounded-full shadow-none hover:shadow-none bg-white border border-logoYellow"
                                  onClick={() => onImageRemove(index)}
                                >
                                  <FiTrash className="w-3 h-3 text-black" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ReactImageUploading>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default AddBlog;
